<template>
    <div class="tw:px-6 tw:py-6">
        <!-- Error state -->
        <div v-if="error" class="tw:mb-6 tw:bg-red-50 tw:border tw:border-red-200 tw:rounded-lg tw:p-4 tw:flex tw:items-center tw:gap-3">
            <span class="tw:text-sm tw:text-red-700">{{ error }}</span>
            <Button variant="danger" @click="refresh">
                {{ $t('back.dashboard.retry') }}
            </Button>
        </div>

        <!-- Loading skeleton -->
        <template v-if="loading">
            <div class="tw:border tw:border-gray-200 tw:rounded-lg tw:p-5 tw:bg-white">
                <div v-for="i in 5" :key="i" class="tw:h-8 tw:bg-gray-100 tw:animate-pulse tw:rounded tw:mb-2"></div>
            </div>
        </template>

        <!-- Data -->
        <template v-else-if="matchesList.length > 0">
            <h3 class="tw:text-lg tw:font-semibold tw:text-gray-900 tw:mb-1">{{ $t('back.dashboard.matches.section_title') }}</h3>
            <p class="tw:text-sm tw:text-gray-500 tw:mb-6">{{ $t('back.dashboard.matches.section_description') }}</p>

            <!-- Single-season: flat table with pagination -->
            <template v-if="!isMultiSeason">
                <div class="tw:border tw:border-gray-200 tw:rounded-lg tw:overflow-hidden tw:bg-white">
                    <div class="tw:overflow-x-auto">
                        <table class="tw:min-w-full tw:divide-y tw:divide-gray-200">
                            <thead class="tw:bg-gray-50">
                                <tr>
                                    <th class="tw:px-4 tw:py-3 tw:text-xs tw:font-medium tw:text-gray-500 tw:uppercase tw:tracking-wider tw:text-left">
                                        {{ $t('back.dashboard.matches.col_event') }}
                                    </th>
                                    <th class="tw:px-4 tw:py-3 tw:text-xs tw:font-medium tw:text-gray-500 tw:uppercase tw:tracking-wider tw:text-left">
                                        {{ $t('back.dashboard.matches.col_date') }}
                                    </th>
                                    <th class="tw:px-4 tw:py-3 tw:text-xs tw:font-medium tw:text-gray-500 tw:uppercase tw:tracking-wider tw:text-right tw:cursor-pointer tw:select-none hover:tw:text-gray-700"
                                        @click="setSort('revenue')"
                                    >
                                        {{ $t('back.dashboard.matches.col_revenue') }}
                                        <span v-if="sortField === 'revenue'" class="tw:ml-1">{{ sortOrder === 'desc' ? '↓' : '↑' }}</span>
                                    </th>
                                    <th class="tw:px-4 tw:py-3 tw:text-xs tw:font-medium tw:text-gray-500 tw:uppercase tw:tracking-wider tw:text-right tw:cursor-pointer tw:select-none hover:tw:text-gray-700"
                                        @click="setSort('attendance')"
                                    >
                                        {{ $t('back.dashboard.matches.col_attendance') }}
                                        <span v-if="sortField === 'attendance'" class="tw:ml-1">{{ sortOrder === 'desc' ? '↓' : '↑' }}</span>
                                    </th>
                                    <th class="tw:px-4 tw:py-3 tw:text-xs tw:font-medium tw:text-gray-500 tw:uppercase tw:tracking-wider tw:text-right">
                                        {{ $t('back.dashboard.matches.col_attendance_tickets') }}
                                    </th>
                                    <th class="tw:px-4 tw:py-3 tw:text-xs tw:font-medium tw:text-gray-500 tw:uppercase tw:tracking-wider tw:text-right">
                                        {{ $t('back.dashboard.matches.col_attendance_abonados') }}
                                    </th>
                                    <th class="tw:px-4 tw:py-3 tw:text-xs tw:font-medium tw:text-gray-500 tw:uppercase tw:tracking-wider tw:text-right">
                                        {{ $t('back.dashboard.matches.col_attendance_invitaciones') }}
                                    </th>
                                    <th class="tw:px-4 tw:py-3 tw:text-xs tw:font-medium tw:text-gray-500 tw:uppercase tw:tracking-wider tw:text-right">
                                        {{ $t('back.dashboard.matches.col_online') }}
                                    </th>
                                    <th class="tw:px-4 tw:py-3 tw:text-xs tw:font-medium tw:text-gray-500 tw:uppercase tw:tracking-wider tw:text-right">
                                        {{ $t('back.dashboard.matches.col_taquilla') }}
                                    </th>
                                    <th class="tw:px-4 tw:py-3 tw:text-xs tw:font-medium tw:text-gray-500 tw:uppercase tw:tracking-wider tw:text-right">
                                        {{ $t('back.dashboard.matches.col_invitations') }}
                                    </th>
                                </tr>
                            </thead>
                            <tbody class="tw:divide-y tw:divide-gray-200">
                                <tr v-for="match in matchesList" :key="match.event_id" class="hover:tw:bg-gray-50">
                                    <td class="tw:px-4 tw:py-3 tw:text-sm tw:text-gray-900 tw:font-medium">{{ match.event_name }}</td>
                                    <td class="tw:px-4 tw:py-3 tw:text-sm tw:text-gray-500">{{ formatDate(match.event_date) }}</td>
                                    <td class="tw:px-4 tw:py-3 tw:text-sm tw:text-gray-700 tw:text-right tw:font-medium">{{ formatCurrency(match.total_revenue) }}</td>
                                    <td class="tw:px-4 tw:py-3 tw:text-sm tw:text-gray-700 tw:text-right">{{ formatNumber(match.total_attendance) }}</td>
                                    <td class="tw:px-4 tw:py-3 tw:text-sm tw:text-gray-500 tw:text-right">{{ formatNumber(match.total_attendance_tickets) }}</td>
                                    <td class="tw:px-4 tw:py-3 tw:text-sm tw:text-gray-500 tw:text-right">{{ formatNumber(match.total_attendance_subscribers) }}</td>
                                    <td class="tw:px-4 tw:py-3 tw:text-sm tw:text-gray-500 tw:text-right">{{ formatNumber(match.total_attendance_invitations) }}</td>
                                    <td class="tw:px-4 tw:py-3 tw:text-sm tw:text-gray-500 tw:text-right">{{ formatNumber(match.online_tickets) }}</td>
                                    <td class="tw:px-4 tw:py-3 tw:text-sm tw:text-gray-500 tw:text-right">{{ formatNumber(match.taquilla_tickets) }}</td>
                                    <td class="tw:px-4 tw:py-3 tw:text-sm tw:text-gray-500 tw:text-right">{{ formatNumber(match.invitations) }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <!-- Pagination -->
                    <div v-if="pagination && pagination.last_page > 1" class="tw:flex tw:items-center tw:justify-between tw:px-4 tw:py-3 tw:border-t tw:border-gray-200 tw:bg-gray-50">
                        <div class="tw:text-sm tw:text-gray-500">
                            {{ $t('back.dashboard.matches.page_info', { current: pagination.current_page, total: pagination.last_page }) }}
                        </div>
                        <div class="tw:flex tw:gap-2">
                            <Button
                                variant="pagination"
                                :disabled="currentPage <= 1"
                                @click="goToPage(currentPage - 1)"
                            >
                                {{ $t('back.dashboard.matches.prev') }}
                            </Button>
                            <Button
                                variant="pagination"
                                :disabled="currentPage >= pagination.last_page"
                                @click="goToPage(currentPage + 1)"
                            >
                                {{ $t('back.dashboard.matches.next') }}
                            </Button>
                        </div>
                    </div>
                </div>
            </template>

            <!-- Multi-season: grouped by season with sub-headers -->
            <template v-else>
                <div class="tw:space-y-6">
                    <div v-for="group in groupedBySeason" :key="group.seasonId" class="tw:border tw:border-gray-200 tw:rounded-lg tw:overflow-hidden tw:bg-white">
                        <!-- Season sub-header -->
                        <div class="tw:bg-blue-50 tw:px-4 tw:py-2 tw:border-b tw:border-gray-200">
                            <h4 class="tw:text-sm tw:font-semibold tw:text-blue-700">{{ group.seasonName }}</h4>
                        </div>
                        <div class="tw:overflow-x-auto">
                            <table class="tw:min-w-full tw:divide-y tw:divide-gray-200">
                                <thead class="tw:bg-gray-50">
                                    <tr>
                                        <th class="tw:px-4 tw:py-3 tw:text-xs tw:font-medium tw:text-gray-500 tw:uppercase tw:tracking-wider tw:text-left">{{ $t('back.dashboard.matches.col_event') }}</th>
                                        <th class="tw:px-4 tw:py-3 tw:text-xs tw:font-medium tw:text-gray-500 tw:uppercase tw:tracking-wider tw:text-left">{{ $t('back.dashboard.matches.col_date') }}</th>
                                        <th class="tw:px-4 tw:py-3 tw:text-xs tw:font-medium tw:text-gray-500 tw:uppercase tw:tracking-wider tw:text-right">{{ $t('back.dashboard.matches.col_revenue') }}</th>
                                        <th class="tw:px-4 tw:py-3 tw:text-xs tw:font-medium tw:text-gray-500 tw:uppercase tw:tracking-wider tw:text-right">{{ $t('back.dashboard.matches.col_attendance') }}</th>
                                        <th class="tw:px-4 tw:py-3 tw:text-xs tw:font-medium tw:text-gray-500 tw:uppercase tw:tracking-wider tw:text-right">{{ $t('back.dashboard.matches.col_attendance_tickets') }}</th>
                                        <th class="tw:px-4 tw:py-3 tw:text-xs tw:font-medium tw:text-gray-500 tw:uppercase tw:tracking-wider tw:text-right">{{ $t('back.dashboard.matches.col_attendance_abonados') }}</th>
                                        <th class="tw:px-4 tw:py-3 tw:text-xs tw:font-medium tw:text-gray-500 tw:uppercase tw:tracking-wider tw:text-right">{{ $t('back.dashboard.matches.col_attendance_invitaciones') }}</th>
                                        <th class="tw:px-4 tw:py-3 tw:text-xs tw:font-medium tw:text-gray-500 tw:uppercase tw:tracking-wider tw:text-right">{{ $t('back.dashboard.matches.col_online') }}</th>
                                        <th class="tw:px-4 tw:py-3 tw:text-xs tw:font-medium tw:text-gray-500 tw:uppercase tw:tracking-wider tw:text-right">{{ $t('back.dashboard.matches.col_taquilla') }}</th>
                                        <th class="tw:px-4 tw:py-3 tw:text-xs tw:font-medium tw:text-gray-500 tw:uppercase tw:tracking-wider tw:text-right">{{ $t('back.dashboard.matches.col_invitations') }}</th>
                                    </tr>
                                </thead>
                                <tbody class="tw:divide-y tw:divide-gray-200">
                                    <tr v-for="match in group.matches" :key="match.event_id" class="hover:tw:bg-gray-50">
                                        <td class="tw:px-4 tw:py-3 tw:text-sm tw:text-gray-900 tw:font-medium">{{ match.event_name }}</td>
                                        <td class="tw:px-4 tw:py-3 tw:text-sm tw:text-gray-500">{{ formatDate(match.event_date) }}</td>
                                        <td class="tw:px-4 tw:py-3 tw:text-sm tw:text-gray-700 tw:text-right tw:font-medium">{{ formatCurrency(match.total_revenue) }}</td>
                                        <td class="tw:px-4 tw:py-3 tw:text-sm tw:text-gray-700 tw:text-right">{{ formatNumber(match.total_attendance) }}</td>
                                        <td class="tw:px-4 tw:py-3 tw:text-sm tw:text-gray-500 tw:text-right">{{ formatNumber(match.total_attendance_tickets) }}</td>
                                        <td class="tw:px-4 tw:py-3 tw:text-sm tw:text-gray-500 tw:text-right">{{ formatNumber(match.total_attendance_subscribers) }}</td>
                                        <td class="tw:px-4 tw:py-3 tw:text-sm tw:text-gray-500 tw:text-right">{{ formatNumber(match.total_attendance_invitations) }}</td>
                                        <td class="tw:px-4 tw:py-3 tw:text-sm tw:text-gray-500 tw:text-right">{{ formatNumber(match.online_tickets) }}</td>
                                        <td class="tw:px-4 tw:py-3 tw:text-sm tw:text-gray-500 tw:text-right">{{ formatNumber(match.taquilla_tickets) }}</td>
                                        <td class="tw:px-4 tw:py-3 tw:text-sm tw:text-gray-500 tw:text-right">{{ formatNumber(match.invitations) }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </template>

            <!-- Multi-season breakdown -->
            <div v-if="isMultiSeason && seasonBreakdown?.length" class="tw:border tw:border-gray-200 tw:rounded-lg tw:p-4 tw:bg-white tw:mt-6">
                <h4 class="tw:text-sm tw:font-semibold tw:text-gray-700 tw:mb-3">{{ $t('back.dashboard.economic.breakdown_title') }}</h4>
                <table class="tw:w-full tw:text-sm">
                    <thead>
                        <tr class="tw:text-left tw:text-gray-500 tw:text-xs">
                            <th class="tw:pb-2">{{ $t('back.dashboard.season') }}</th>
                            <th class="tw:pb-2 tw:text-right">{{ $t('back.dashboard.matches.col_match_count') }}</th>
                            <th class="tw:pb-2 tw:text-right">{{ $t('back.dashboard.matches.col_revenue') }}</th>
                            <th class="tw:pb-2 tw:text-right">{{ $t('back.dashboard.matches.col_attendance') }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="row in seasonBreakdown" :key="row.season" class="tw:border-t tw:border-gray-100">
                            <td class="tw:py-1.5">{{ row.season }}</td>
                            <td class="tw:py-1.5 tw:text-right">{{ row.matchCount }}</td>
                            <td class="tw:py-1.5 tw:text-right">{{ formatCurrency(row.totalRevenue) }}</td>
                            <td class="tw:py-1.5 tw:text-right">{{ formatNumber(row.totalAttendance) }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </template>

        <!-- Empty state -->
        <div v-else class="tw:bg-gray-50 tw:border tw:border-gray-200 tw:rounded-lg tw:p-8 tw:text-center">
            <p class="tw:text-sm tw:text-gray-500">{{ $t('back.dashboard.matches.empty') }}</p>
        </div>
    </div>
</template>

<script setup>
    import { toRef } from 'vue';
    import { useMatches } from '@/composables/admin/dashboard/useMatches';
    import { formatCurrency, formatNumber, formatDate } from '@/utils/format';
    import Button from '@/modules/admin/ui/Button.vue';

    const props = defineProps({
        seasonIds: { type: Array, required: true },
        seasons: { type: Array, required: true },
    });


    const seasonIdsRef = toRef(props, 'seasonIds');
    const seasonsRef = toRef(props, 'seasons');

    const {
        data,
        loading,
        error,
        isMultiSeason,
        matchesList,
        pagination,
        sortField,
        sortOrder,
        currentPage,
        setSort,
        goToPage,
        seasonBreakdown,
        groupedBySeason,
        refresh,
    } = useMatches(seasonIdsRef, seasonsRef);
</script>
